import { type TabsProps } from "antd";
import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useLocation, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useQueryState } from "nuqs";
import { useTranslation } from "react-i18next";
import { useFinancialAnalytics } from "../../../../../api/services/financial-integration.hooks";
import {
  getPreviousPathname,
  symbolOpenSource,
  trackSymbolOpened,
} from "../../../../../analytics/productEvents";
import { RecommendCard } from "./components/RecommendCard";
import { normalizeHalalStatus } from "./components/ShariahReport/shariahReportHalalStatus";
import { LoadingSpinner } from "../../../../../router/LoadingSpinner";
import { lazyWithRetry } from "../../../../../utils/lazyWithRetry";
import { preloadTradingViewScript } from "../../../../../utils/tradingViewLoader";
import EarningsTab, { type EarningsSubView } from "./components/EarningsTab";
import RatingTab, { type RatingSubView } from "./components/RatingTab";
import Tabs from "./components/Tabs";
import Summary from "./components/Summary";

const Financials = lazyWithRetry(() => import("./components/Financials"));
const ShariahReport = lazyWithRetry(() => import("./components/ShariahReport"));
const Ranking = lazyWithRetry(() => import("./components/Ranking"));
const Swot = lazyWithRetry(() => import("./components/Swot"));
const Ownership = lazyWithRetry(() => import("./components/Ownership"));
const Peers = lazyWithRetry(() => import("./components/Peers"));

const STOCK_TAB_KEYS = new Set(["1", "2", "3", "4", "5", "6", "7", "8", "9"]);

/**
 * The stock page. Rendered by src/routes/symbol.tsx, whose loader handles the
 * uppercase redirect, 404s, metadata and the server-side data.
 */
export default function TopHalalStockInfo() {
  const { t } = useTranslation();
  const { symbol } = useParams<{ symbol: string }>();
  const location = useLocation();
  const queryClient = useQueryClient();
  const prevSymbolRef = useRef<string | undefined>(symbol);
  const trackedSymbolRef = useRef<string | null>(null);

  const {
    data: analyticsData,
    isFetching: isAnalyticsFetching,
    isError: isAnalyticsError,
  } = useFinancialAnalytics({ symbol: symbol! }, { enabled: Boolean(symbol) });



  useEffect(() => {
    if (!symbol) return;
    if (trackedSymbolRef.current === symbol) return;
    if (isAnalyticsFetching && !analyticsData && !isAnalyticsError) return;

    const openState = location.state as {
      symbolSource?: string;
      rowPosition?: number;
    } | null;
    trackedSymbolRef.current = symbol;
    trackSymbolOpened({
      symbol: (analyticsData?.data?.symbol ?? symbol).toUpperCase(),
      halal_status: normalizeHalalStatus(analyticsData?.data?.halal_status),
      source: openState?.symbolSource || symbolOpenSource(getPreviousPathname()),
      row_position:
        typeof openState?.rowPosition === "number"
          ? openState.rowPosition
          : undefined,
    });
  }, [
    symbol,
    analyticsData,
    isAnalyticsFetching,
    isAnalyticsError,
    location.state,
  ]);

  const [activeTab, setActiveTab] = useQueryState("tab", {
    defaultValue: "1",
    parse: (value) =>
      value && STOCK_TAB_KEYS.has(value) ? value : "1",
    serialize: (value) => value,
  });

  const [ratingView, setRatingView] = useQueryState("ratingView", {
    defaultValue: "forecast",
    parse: (value) => (value === "valuation" ? "valuation" : "forecast"),
    serialize: (value) => value,
  });

  const [earningsView, setEarningsView] = useQueryState("earningsView", {
    defaultValue: "earnings",
    parse: (value) => (value === "dividends" ? "dividends" : "earnings"),
    serialize: (value) => value,
  });

  const legacyTabMigrated = useRef(false);

  useEffect(() => {
    if (legacyTabMigrated.current || typeof window === "undefined") return;
    legacyTabMigrated.current = true;

    const params = new URLSearchParams(window.location.search);
    const tab = params.get("tab");
    if (!tab) return;

    // Pre-Shariah tab layout had Peers at 10 (now 9). Its old Dividends tab
    // was also 9, but 9 is Peers now, so ?tab=9 is left alone.
    if (tab === "10") void setActiveTab("9");
  }, [setActiveTab]);

  useEffect(() => {
    preloadTradingViewScript();
  }, []);

  useEffect(() => {
    if (prevSymbolRef.current !== symbol && prevSymbolRef.current) {
      const oldSymbol = prevSymbolRef.current;
      queryClient.cancelQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey.some((k) => k === oldSymbol),
      });
    }
    prevSymbolRef.current = symbol;
  }, [symbol, queryClient]);

  const onChange = useCallback(
    (key: string) => {
      void setActiveTab(key);
    },
    [setActiveTab],
  );

  const items: TabsProps["items"] = useMemo(
    () => [
      {
        key: "1",
        label: t("pages.stock.tabs.summary"),
        children: <Summary />,
      },
      {
        key: "2",
        label: t("pages.stock.tabs.rating"),
        children: (
          <Suspense fallback={<LoadingSpinner />}>
            <RatingTab
              ratingView={ratingView as RatingSubView}
              onRatingViewChange={(v: RatingSubView) => void setRatingView(v)}
            />
          </Suspense>
        ),
      },
      {
        key: "3",
        label: t("pages.stock.tabs.financials"),
        children: (
          <Suspense fallback={<LoadingSpinner />}>
            <Financials />
          </Suspense>
        ),
      },
      {
        key: "4",
        label: t("pages.stock.tabs.shariahReport"),
        children: (
          <Suspense fallback={<LoadingSpinner />}>
            <ShariahReport />
          </Suspense>
        ),
      },
      {
        key: "5",
        label: t("pages.stock.tabs.ranking"),
        children: (
          <Suspense fallback={<LoadingSpinner />}>
            <Ranking />
          </Suspense>
        ),
      },
      {
        key: "6",
        label: t("pages.stock.tabs.swot"),
        children: (
          <Suspense fallback={<LoadingSpinner />}>
            <Swot />
          </Suspense>
        ),
      },
      {
        key: "7",
        label: t("pages.stock.tabs.ownership"),
        children: (
          <Suspense fallback={<LoadingSpinner />}>
            <Ownership />
          </Suspense>
        ),
      },
      {
        key: "8",
        label: t("pages.stock.tabs.earnings"),
        children: (
          <Suspense fallback={<LoadingSpinner />}>
            <EarningsTab
              earningsView={earningsView as EarningsSubView}
              onEarningsViewChange={(v: EarningsSubView) =>
                void setEarningsView(v)
              }
            />
          </Suspense>
        ),
      },
      {
        key: "9",
        label: t("pages.stock.tabs.peers"),
        children: (
          <Suspense fallback={<LoadingSpinner />}>
            <Peers />
          </Suspense>
        ),
      },
    ],
    [t, ratingView, setRatingView, earningsView, setEarningsView],
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
      <Suspense fallback={<LoadingSpinner />}>
        <RecommendCard />
      </Suspense>
      <Tabs
          activeKey={activeTab}
          items={items}
          onChange={onChange}
          skeletonCount={9}
        />
    </div>
  );
}
